import { graphql } from '@/__generated__';
import { GroupFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

type Result = ReturnType<typeof useGroupsByIds>;

export type GroupDefinitionListNode = NonNullable<Result['data']>[number];

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetGroupsByUniqueId = graphql(/* GraphQL */ `
  query Groups(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: GroupFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        groups(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              name
              uniqueId
              modelCount
              ownerName
              ownerEmail
              ownerSlack
              ownerGithub
            }
          }
        }
      }
    }
  }
`);

type UseGroupsParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  filter: GroupFilter | undefined;
  pause?: boolean;
};

export const useGroups = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: UseGroupsParams) => {
  const query = usePaginatedQuery(
    {
      query: GetGroupsByUniqueId,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause: pause || !environmentId || !first || userId === 'loading',
    },
    (data) => {
      const groups = data.environment.definition?.groups;
      const pageInfo = groups?.pageInfo;
      if (groups == null || pageInfo == null) return undefined;
      return {
        totalCount: groups.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.definition?.groups.edges.map((edge) => edge.node);
    },
    (data) => data.environment.definition?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};

type GroupByEnvironmentParams = Omit<UseGroupsParams, 'filter'> & {
  uniqueIds?: string[];
};

/**
 * Groups are Definition Only
 */
export const useGroupsByIds = ({
  first,
  after,
  environmentId,
  userId,
  uniqueIds,
  pause,
}: GroupByEnvironmentParams) => {
  return useGroups({
    first,
    after,
    environmentId,
    userId,
    filter: { uniqueIds: uniqueIds ?? null },
    pause: pause || uniqueIds?.length === 0,
  });
};
